import React, { useMemo, useRef } from 'react';

import type { Invitation } from '@sine/sine-core-api-types';
import type { JoiForm } from '@sine/sine-ui-components';
import { Button, ButtonGroup, CheckBox, Flex, Loader, Modal } from '@sine/sine-ui-components';

import type { PassWithResponseSummaries } from '../../../activity/helpers/activityFeedCache.helpers';
import SigninProvider, { useSignIn } from '../SigninProvider';
import { generatePrefillPassFromInvitation, generatePrefillPassFromPass } from '../helpers/prefillPass';
import { useCheckInWorkflow } from '../hooks/useCheckInWorkflow';
import SiteCheckInForm from './SiteCheckInForm';
import VisitorDetailsForm from './VisitorDetailsForm';
import type { VisitorDetailsFormValues } from './visitorCheckInModal.types';

interface VisitorCheckInModalProps {
  invitation?: Invitation;
  pass?: PassWithResponseSummaries;
  show: boolean;
  onClose: () => void;
}

function VisitorCheckInModalInner({ invitation, pass, show, onClose }: VisitorCheckInModalProps) {
  const { isLoading, siteRequirements, checkInAnother, setCheckInAnother, selectedVisitorType } = useSignIn();

  const visitorDetailsFormRef = useRef<JoiForm<VisitorDetailsFormValues>>();
  const checkInFormRef = useRef<JoiForm<any>>();
  const firstNameInput = useRef<HTMLInputElement>(null);

  const prefillPass = useMemo(
    () => (invitation ? generatePrefillPassFromInvitation(invitation) : generatePrefillPassFromPass(pass)),
    [invitation, pass],
  );

  const {
    page,
    prefilledForm,
    hasCheckInForm,
    formRequired,
    formResetKey,
    isSubmitting,
    onNext,
    onCheckIn,
    onBack,
    PAGE,
  } = useCheckInWorkflow({
    invitation,
    pass,
    prefillPass,
    onClose,
    visitorDetailsFormRef,
    checkInFormRef,
    firstNameInput,
  });

  return (
    <>
      <Modal
        show={show}
        size='md'
        onClose={onClose}
        header={(page === PAGE.CHECK_IN_FORM && siteRequirements?.form?.name) || 'Visitor Check-in'}
        footer={
          <Flex justifyContent={!!pass || !!invitation ? 'flex-end' : 'space-between'} alignItems='center'>
            {!invitation && !pass && (
              <CheckBox
                label='Check-in another guest'
                onChange={setCheckInAnother}
                value={checkInAnother}
                data-st-ext='checkbox-another-guest'
                noSpaceReservation
              />
            )}

            <ButtonGroup alignment='right'>
              {page === PAGE.VISITOR_DETAILS && (
                <Button
                  variant='tertiary'
                  onClick={onClose}
                  data-st-ext='button-cancel'
                  disabled={isLoading || isSubmitting}
                >
                  Cancel
                </Button>
              )}

              {page === PAGE.CHECK_IN_FORM && (
                <Button
                  variant='tertiary'
                  onClick={onBack}
                  data-st-ext='button-back'
                  disabled={isLoading || isSubmitting}
                >
                  Back
                </Button>
              )}

              {page === PAGE.VISITOR_DETAILS && hasCheckInForm && formRequired && (
                <Button
                  variant='primary'
                  onClick={onNext}
                  data-st-ext='button-next'
                  disabled={isLoading || isSubmitting}
                >
                  Next
                </Button>
              )}

              {((page === PAGE.VISITOR_DETAILS && !hasCheckInForm) || !formRequired || page === PAGE.CHECK_IN_FORM) && (
                <Button
                  variant='primary'
                  onClick={onCheckIn}
                  data-st-ext='button-check-in'
                  disabled={isLoading || isSubmitting}
                >
                  Check-in
                </Button>
              )}
            </ButtonGroup>
          </Flex>
        }
      >
        <Loader overlay show={isLoading} />

        <VisitorDetailsForm
          key={`visitor-details-${formResetKey}`}
          formRef={visitorDetailsFormRef}
          pass={prefillPass}
          firstNameInput={firstNameInput}
          hideForm={page === PAGE.CHECK_IN_FORM}
        />
        <SiteCheckInForm
          key={`site-checkin-${formResetKey}`}
          formRef={checkInFormRef}
          prefilledForm={prefilledForm}
          hideForm={page === PAGE.VISITOR_DETAILS}
          visitorTypeId={selectedVisitorType?.value as string}
          siteRequirements={siteRequirements}
        />
      </Modal>
    </>
  );
}

export default function VisitorCheckInModal({ invitation, pass, show, onClose }: VisitorCheckInModalProps) {
  return (
    <SigninProvider invitation={invitation} pass={pass}>
      <VisitorCheckInModalInner invitation={invitation} pass={pass} show={show} onClose={onClose} />
    </SigninProvider>
  );
}
